import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { adGroupService } from "../api/services/adGroupService";
import {
  AdGroupFilters,
  AdGroupsResponse,
  UpdateAdGroupPayload,
} from "../schemas/adGroupSchema";

export const useAdGroups = (
  filters: AdGroupFilters = {},
  options?: { enabled?: boolean },
) => {
  const queryClient = useQueryClient();

  const {
    data: adGroups,
    isLoading: isAdGroupsLoading,
    refetch,
  } = useQuery<AdGroupsResponse, Error>({
    queryKey: ["adGroups", filters],
    queryFn: () => adGroupService.getAll(filters),
    enabled: options?.enabled !== false,
  });

  const createAdGroupMutation = useMutation({
    mutationFn: adGroupService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adGroups"] });
      queryClient.invalidateQueries({ queryKey: ["adGroupOptions"] });
      toast.success("Ad Group created successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to create Ad Group");
    },
  });

  const updateAdGroupMutation = useMutation({
    mutationFn: adGroupService.update,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adGroups"] });
      queryClient.invalidateQueries({ queryKey: ["adGroupOptions"] });
      toast.success("Ad Group updated successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to update Ad Group");
    },
  });

  const deleteAdGroupMutation = useMutation({
    mutationFn: adGroupService.remove,
    onSuccess: (_data, ids) => {
      queryClient.invalidateQueries({ queryKey: ["adGroups"] });
      queryClient.invalidateQueries({ queryKey: ["adGroupOptions"] });
      if (Array.isArray(ids) && ids.length > 1) {
        toast.success(`${ids.length} items deleted successfully`);
        return;
      }
      toast.success("Ad Group deleted successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to delete Ad Group");
    },
  });

  return {
    adGroups,
    refetchAdGroups: refetch,
    createAdGroup: createAdGroupMutation.mutate,
    updateAdGroup: updateAdGroupMutation.mutate,
    deleteAdGroups: deleteAdGroupMutation.mutate,
    isLoading:
      isAdGroupsLoading ||
      createAdGroupMutation.isPending ||
      updateAdGroupMutation.isPending ||
      deleteAdGroupMutation.isPending,
  };
};
